import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { api, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      // Backend handles both authenticated and guest carts
      const response = await api.get('/cart/');
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      // Set empty cart on error
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (sectionId, learnerId, includeKit = false, kitDeliveryMethod = 'pickup') => {
    try {
      setLoading(true);
      await api.post('/cart/items', { 
        section_id: sectionId, 
        learner_id: learnerId,
        include_kit: includeKit,
        kit_delivery_method: kitDeliveryMethod
      });
      await fetchCart();
      toast.success('Added to cart!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add to cart';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (sectionId, learnerId, updates) => {
    try {
      setLoading(true);
      await api.patch(`/cart/items/${sectionId}/${learnerId}`, updates);
      await fetchCart();
      toast.success('Cart updated');
    } catch (error) {
      toast.error('Failed to update cart');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (sectionId, learnerId) => {
    try {
      setLoading(true);
      await api.delete(`/cart/items/${sectionId}/${learnerId}`);
      await fetchCart();
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeCampFromCart = async (campSectionId, learnerId, guestStudentName = null) => {
    try {
      setLoading(true);
      await api.post('/cart/remove-camp-item', {
        camp_section_id: campSectionId,
        learner_id: learnerId,
        // For guest items learner_id is null on every cart row, so the backend
        // also de-dups on guest_student_name. Without this, removing 1 of 2
        // sibling guest items would wipe both. (Iter 57c regression fix.)
        guest_student_name: guestStudentName,
      });
      await fetchCart();
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await api.delete('/cart/clear');
      setCart({ items: [], total: 0 });
      toast.success('Cart cleared');
    } catch (error) {
      toast.error('Failed to clear cart');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkout = async (shippingAddress = null, guestInfo = null) => {
    try {
      setLoading(true);
      const response = await api.post('/payments/checkout/session', {
        origin_url: window.location.origin,
        shipping_address: shippingAddress,
        ...(guestInfo || {}),
      });
      return response.data;
    } catch (error) {
      if (!error.response?.data?.error_code) {
        const message = error.response?.data?.detail || 'Checkout failed';
        toast.error(message);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Calculate if any items need shipping
  const hasShippableItems = cart.items?.some(item => 
    item.include_kit && item.kit_delivery_method === 'ship'
  ) || false;

  const value = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    removeCampFromCart,
    clearCart,
    checkout,
    refreshCart: fetchCart,
    itemCount: cart.items?.length || 0,
    hasShippableItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
